import { theme, fontStack } from "../theme";
import type { ReportProductStatus, ReportType } from "@livemessage/protocol";

/**
 * Chemin d'avancement du dossier — le repère n°1 du modérateur (demande d'Henri,
 * v13.11) : « où en est ce dossier ? » doit se lire d'un coup d'œil, sans jargon
 * et sans connaître l'informatique.
 *
 * Forme retenue (maquette Henri) : des CHEVRONS enchaînés, façon « parcours » —
 * étape franchie en plein vert avec une coche, étape courante en plein contrasté,
 * étapes à venir en creux. Les libellés sont en français courant : aucun statut
 * technique (ACTIONED, DISMISSED…) n'apparaît à l'écran.
 *
 * La dernière étape CHANGE DE NOM selon l'issue réelle, parce qu'un modérateur ne
 * doit pas avoir à traduire : « Sanction », « Aucune sanction » (signalement) ou
 * « Traité » (ticket/demande). Tant que le dossier est ouvert, elle annonce ce
 * qu'il reste à faire (« Décision » / « Traitement »).
 */

/** Une étape du parcours, telle que l'écran la montre. */
export interface PathStep {
  key: string;
  label: string;
  /** franchie (passée), courante, ou à venir. */
  state: "done" | "current" | "todo";
}

/** Les statuts qui closent un dossier (plus rien à décider). */
const TERMINAL: ReportProductStatus[] = [
  "ACTIONED",
  "DISMISSED",
  "RESOLVED",
  "PLANNED",
  "DECLINED",
];

export function isTerminalStatus(status: ReportProductStatus): boolean {
  return TERMINAL.includes(status);
}

/**
 * Construit le parcours affiché pour un dossier.
 *
 * Le rang d'avancement est volontairement GROSSIER (reçu → analyse → fin) : c'est
 * une vue de progression, pas la machine à états (elle, c'est `allowedReportTransitions`,
 * qui reste la seule autorité sur ce qui est permis).
 */
export function buildPath(
  status: ReportProductStatus,
  type: ReportType
): PathStep[] {
  const isTicket = type !== "REPORT_CONTENT";
  const done = isTerminalStatus(status);

  // Libellé de l'étape finale : on NOMME l'issue quand elle est connue.
  let finalLabel: string;
  if (status === "ACTIONED") finalLabel = "Sanction";
  else if (status === "DISMISSED") finalLabel = "Aucune sanction";
  else if (status === "RESOLVED") finalLabel = "Traité";
  else if (status === "PLANNED") finalLabel = "Planifié";
  else if (status === "DECLINED") finalLabel = "Refusé";
  else finalLabel = isTicket ? "Traitement" : "Décision";

  const steps: PathStep[] = [
    {
      key: "received",
      label: "Reçu",
      state: status === "RECEIVED" ? "current" : "done",
    },
    {
      key: "review",
      label: "En cours d'analyse",
      state:
        status === "IN_REVIEW"
          ? "current"
          : status === "RECEIVED"
            ? "todo"
            : "done",
    },
  ];

  // Étape latérale : n'apparaît que si elle est (ou a été) d'actualité — un
  // parcours qui montrerait toujours « Complément demandé » ferait croire à une
  // étape obligatoire.
  if (status === "NEEDS_INFO") {
    steps.push({ key: "needs-info", label: "Complément demandé", state: "current" });
  }

  steps.push({
    key: "final",
    label: finalLabel,
    state: done ? "done" : "todo",
  });

  return steps;
}

/**
 * Rendu des chevrons. Desktop d'abord (la console est un outil de bureau) :
 * les étapes se répartissent sur toute la largeur ; sous ~700px elles passent
 * à la ligne au lieu de se comprimer illisiblement.
 */
export function StatusPath({ steps }: { steps: PathStep[] }) {
  const NOTCH = 14; // profondeur de la pointe du chevron, en px

  return (
    <div
      role="list"
      aria-label="Avancement du dossier"
      style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "stretch" }}
    >
      {steps.map((step, i) => {
        const first = i === 0;
        const last = i === steps.length - 1;

        // Chevron : pointe à droite (sauf le dernier), encoche à gauche (sauf le
        // premier) — c'est ce qui donne l'enchaînement « → → → » de la maquette.
        const clip = first
          ? `polygon(0 0, calc(100% - ${NOTCH}px) 0, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0 100%)`
          : last
            ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${NOTCH}px 50%)`
            : `polygon(0 0, calc(100% - ${NOTCH}px) 0, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0 100%, ${NOTCH}px 50%)`;

        // Trois APLATS francs, comme la maquette : la forme du chevron doit se
        // lire d'elle-même. (Un liseré `inset` ne suit pas le `clip-path` — il
        // redessinait un rectangle par-dessus la pointe.)
        const palette =
          step.state === "done"
            ? { bg: theme.accent, fg: theme.onAccent, weight: 600 }
            : step.state === "current"
              ? { bg: theme.ink, fg: theme.bg, weight: 700 }
              : { bg: theme.surface3, fg: theme.ink3, weight: 400 };

        return (
          <div
            key={step.key}
            role="listitem"
            aria-current={step.state === "current" ? "step" : undefined}
            title={
              step.state === "done"
                ? `${step.label} — étape franchie`
                : step.state === "current"
                  ? `${step.label} — étape en cours`
                  : `${step.label} — à venir`
            }
            style={{
              flex: "1 1 150px",
              minWidth: 130,
              background: palette.bg,
              color: palette.fg,
              clipPath: clip,
              padding: `11px ${last ? 18 : NOTCH + 18}px 11px ${first ? 18 : NOTCH + 14}px`,
              fontFamily: fontStack.uiSemiBold,
              fontWeight: palette.weight,
              fontSize: 13.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {step.state === "done" && <span aria-hidden>✓</span>}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}
